import type {
  InventoryCountArea,
  InventoryCountHistoryQuery,
  InventoryCountHistoryResponse,
  InventoryCountReviewResponse,
  PostStockCountRequest,
  PostStockCountResponse,
} from "@medicorepay/shared";
import { useEffect, useMemo, useRef, useState } from "react";

export interface AreaStockCountViewProps {
  getAreas: () => Promise<InventoryCountArea[]>;
  getReview: (area: string) => Promise<InventoryCountReviewResponse>;
  postCount: (
    body: PostStockCountRequest,
    idempotencyKey: string,
  ) => Promise<PostStockCountResponse>;
  getHistory?: (
    query: InventoryCountHistoryQuery,
  ) => Promise<InventoryCountHistoryResponse>;
  onBack: () => void;
  idempotencyScope?: string;
}

interface PendingCountCommand {
  fingerprint: string;
  idempotencyKey: string;
}

interface SavedCountDraft {
  counts: Record<string, string>;
  updatedAt: string;
}

const DEFAULT_PAGE_SIZE = 25;

function newCommandKey() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `count-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function errorMessage(error: unknown) {
  const candidate = error as {
    response?: { data?: { message?: string; code?: string } };
    message?: string;
  };
  if (candidate.response?.data?.code === "COUNT_CONCURRENT_CHANGE") {
    return "Stock changed after counting. Reload this area and review the latest expected quantities.";
  }
  return (
    candidate.response?.data?.message ||
    candidate.message ||
    "Unable to post the stock count"
  );
}

function quantity(value: string | number) {
  return Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

function reasonLabel(reason: string) {
  return reason
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function AreaStockCountView({
  getAreas,
  getReview,
  postCount,
  getHistory,
  onBack,
  idempotencyScope = "active-store",
}: AreaStockCountViewProps) {
  const [areas, setAreas] = useState<InventoryCountArea[]>([]);
  const [review, setReview] = useState<InventoryCountReviewResponse | null>(
    null,
  );
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [reviewing, setReviewing] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);
  const [reason, setReason] = useState("ROUTINE_AREA_COUNT");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posted, setPosted] = useState<Record<string, PostStockCountResponse>>(
    {},
  );
  const [completed, setCompleted] = useState<PostStockCountResponse[] | null>(
    null,
  );
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<InventoryCountHistoryResponse | null>(
    null,
  );
  const [historySearch, setHistorySearch] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const pendingCommands = useRef<Record<string, PendingCountCommand> | null>(
    null,
  );
  const storageKey = `medicorepay:stock-count:${idempotencyScope}:pending`;
  const draftStorageKey = (area: string) =>
    `medicorepay:stock-count:${idempotencyScope}:draft:${encodeURIComponent(area)}`;

  const saveDraft = (
    area: string,
    nextCounts: Record<string, string>,
    showStatus = true,
  ) => {
    try {
      const entered = Object.fromEntries(
        Object.entries(nextCounts).filter(([, value]) => value.trim() !== ""),
      );
      if (Object.keys(entered).length === 0) {
        localStorage.removeItem(draftStorageKey(area));
        if (showStatus) setDraftStatus("Draft cleared");
        return;
      }
      const draft: SavedCountDraft = {
        counts: entered,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(draftStorageKey(area), JSON.stringify(draft));
      if (showStatus) {
        setDraftStatus(
          `Draft saved locally for ${Object.keys(entered).length} counted lot${Object.keys(entered).length === 1 ? "" : "s"}`,
        );
      }
    } catch {
      if (showStatus) {
        setError("Unable to save this draft in the browser");
      }
    }
  };

  const loadAreas = async () => {
    setLoading(true);
    setError(null);
    try {
      setAreas(await getAreas());
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (page = 1, search = historySearch) => {
    if (!getHistory) return;
    setShowHistory(true);
    setHistoryLoading(true);
    setError(null);
    try {
      setHistory(await getHistory({ page, limit: DEFAULT_PAGE_SIZE, search }));
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    void loadAreas();
    // Loader functions are stable app adapters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAreas]);

  const lines = useMemo(() => {
    if (!review) return [];
    return review.lots.map((lot) => {
      const raw = counts[lot.lotId];
      const physical =
        raw === undefined || raw.trim() === "" ? null : Number(raw);
      const expected = Number(lot.expectedQuantity);
      return {
        lot,
        raw,
        physical,
        expected,
        variance: physical === null ? null : physical - expected,
      };
    });
  }, [counts, review]);
  const countedLines = lines.filter(
    (line) => line.raw !== undefined && line.raw.trim() !== "",
  );
  const varianceLines = countedLines.filter(
    (line) =>
      line.variance !== null &&
      Number.isFinite(line.variance) &&
      Math.abs(line.variance) > 0.0005,
  );
  const normalizedSearch = search.trim().toLowerCase();
  const filteredLines = lines.filter((line) => {
    if (!normalizedSearch) return true;
    return [line.lot.productName, line.lot.sku, line.lot.batchNumber].some(
      (value) => value.toLowerCase().includes(normalizedSearch),
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredLines.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visibleLines = reviewing
    ? countedLines
    : filteredLines.slice((safePage - 1) * pageSize, safePage * pageSize);

  const openArea = async (area: string) => {
    setLoading(true);
    setError(null);
    setCompleted(null);
    try {
      const loaded = await getReview(area);
      const validLotIds = new Set(loaded.lots.map((lot) => lot.lotId));
      let restoredCounts: Record<string, string> = {};
      try {
        const stored = localStorage.getItem(draftStorageKey(area));
        if (stored) {
          const draft = JSON.parse(stored) as SavedCountDraft;
          restoredCounts = Object.fromEntries(
            Object.entries(draft.counts ?? {}).filter(
              ([lotId, value]) =>
                validLotIds.has(lotId) && typeof value === "string",
            ),
          );
        }
      } catch {
        // Ignore an unreadable local draft and start a fresh count.
      }
      setReview(loaded);
      setCounts(restoredCounts);
      setReviewing(false);
      setSearch("");
      setPage(1);
      setDraftStatus(
        Object.keys(restoredCounts).length > 0
          ? `Restored ${Object.keys(restoredCounts).length} counted lot${Object.keys(restoredCounts).length === 1 ? "" : "s"} from your draft`
          : null,
      );
      setPosted({});
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  };

  const reviewVariances = () => {
    setError(null);
    if (!lines.length) return setError("This area has no countable lots");
    if (!countedLines.length) {
      return setError("Enter a physical quantity for at least one lot");
    }
    if (
      countedLines.some(
        (line) =>
          line.physical === null ||
          !Number.isFinite(line.physical) ||
          line.physical < 0,
      )
    ) {
      return setError("Counted quantities must be valid non-negative numbers");
    }
    if (review) saveDraft(review.area, counts, false);
    setReviewing(true);
  };

  const commandFor = (lotId: string, fingerprint: string) => {
    if (!pendingCommands.current) {
      try {
        const stored = sessionStorage.getItem(storageKey);
        pendingCommands.current = stored
          ? (JSON.parse(stored) as Record<string, PendingCountCommand>)
          : {};
      } catch {
        pendingCommands.current = {};
      }
    }
    const current = pendingCommands.current[lotId];
    if (current?.fingerprint === fingerprint) return current;
    const created = { fingerprint, idempotencyKey: newCommandKey() };
    pendingCommands.current[lotId] = created;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify(pendingCommands.current),
      );
    } catch {
      // The in-memory map still protects retries in this mounted flow.
    }
    return created;
  };

  const postArea = async () => {
    if (!review) return;
    setError(null);
    if (!reason.trim()) return setError("Enter a count reason");
    setPosting(true);
    const nextPosted = { ...posted };
    try {
      for (const line of countedLines) {
        if (nextPosted[line.lot.lotId]) continue;
        const body: PostStockCountRequest = {
          lotId: line.lot.lotId,
          physicalQuantity: line.physical!.toFixed(3),
          expectedBalanceVersion: line.lot.balanceVersion,
          reason,
        };
        const fingerprint = JSON.stringify(body);
        const command = commandFor(line.lot.lotId, fingerprint);
        const result = await postCount(body, command.idempotencyKey);
        nextPosted[line.lot.lotId] = result;
        setPosted({ ...nextPosted });
      }
      setCompleted(Object.values(nextPosted));
      pendingCommands.current = null;
      try {
        sessionStorage.removeItem(storageKey);
        localStorage.removeItem(draftStorageKey(review.area));
      } catch {
        // All server commands are already complete.
      }
    } catch (postError) {
      setError(`${errorMessage(postError)} Retry keeps each lot command safe.`);
    } finally {
      setPosting(false);
    }
  };

  if (completed && review) {
    const changed = completed.filter(
      (result) => Math.abs(Number(result.variance)) > 0.0005,
    ).length;
    const replayed = completed.filter((result) => result.replayed).length;
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-xl border border-green-200 bg-white p-8 shadow-sm">
          <div className="text-sm font-semibold uppercase tracking-wide text-green-700">
            Area count posted
          </div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            {review.area}
          </h1>
          <p className="mt-2 text-gray-600">
            {completed.length} counted {completed.length === 1 ? "lot" : "lots"}{" "}
            reviewed; {changed} inventory variances posted.
          </p>
          {replayed > 0 && (
            <p className="mt-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
              {replayed} lot commands were safe replays; no duplicate variance
              was posted.
            </p>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            {getHistory && (
              <button
                type="button"
                onClick={() => {
                  setCompleted(null);
                  setReview(null);
                  void loadHistory();
                }}
                className="rounded-lg bg-green-700 px-5 py-2.5 font-medium text-white hover:bg-green-800"
              >
                View count history
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setCompleted(null);
                setReview(null);
                void loadAreas();
              }}
              className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700"
            >
              Count another area
            </button>
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-gray-300 px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              Back to stock
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {showHistory ? "Stock count history" : "Area stock count"}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {showHistory
              ? "Review every posted physical count, including counts with no variance."
              : "Choose a rack or area, find the lots you counted, review their variances, then post."}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {getHistory && (
            <button
              type="button"
              onClick={() => {
                if (showHistory) {
                  setShowHistory(false);
                  setError(null);
                } else {
                  void loadHistory();
                }
              }}
              className="text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              {showHistory ? "Back to counting" : "View count history"}
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-medium text-blue-700 hover:text-blue-900"
          >
            Back to stock
          </button>
        </div>
      </header>
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}
      {showHistory ? (
        <section className="rounded-xl bg-white p-5 shadow-sm">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void loadHistory(1, historySearch);
            }}
          >
            <label className="min-w-[260px] flex-1 text-xs font-medium uppercase tracking-wide text-gray-600">
              Find a posted count
              <input
                aria-label="Search stock count history"
                type="search"
                value={historySearch}
                onChange={(event) => setHistorySearch(event.target.value)}
                placeholder="Search product, SKU, batch, reason or staff"
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal"
              />
            </label>
            <button
              type="submit"
              disabled={historyLoading}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Search
            </button>
            {historySearch && (
              <button
                type="button"
                onClick={() => {
                  setHistorySearch("");
                  void loadHistory(1, "");
                }}
                className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </form>
          {historyLoading && !history ? (
            <p className="py-10 text-center text-sm text-gray-500">
              Loading stock count history…
            </p>
          ) : history && history.items.length > 0 ? (
            <>
              <div className="mt-5 overflow-x-auto">
                <table className="w-full min-w-[1100px] text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-3">Counted at</th>
                      <th>Product</th>
                      <th>Batch / area</th>
                      <th className="text-right">Expected</th>
                      <th className="text-right">Physical</th>
                      <th className="text-right">Variance</th>
                      <th>Reason</th>
                      <th>Counted by</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.items.map((item) => {
                      const variance = Number(item.variance);
                      return (
                        <tr key={item.id}>
                          <td className="whitespace-nowrap px-3 py-3">
                            {new Date(item.countedAt).toLocaleString("en-IN")}
                          </td>
                          <td>
                            <strong>{item.productName}</strong>
                            <div className="text-xs text-gray-500">
                              {item.sku}
                            </div>
                          </td>
                          <td>
                            {item.batchNumber}
                            <div className="text-xs text-gray-500">
                              {item.area}
                            </div>
                          </td>
                          <td className="text-right">
                            {quantity(item.priorQuantity)} {item.unit}
                          </td>
                          <td className="text-right">
                            {quantity(item.physicalQuantity)} {item.unit}
                          </td>
                          <td
                            className={`text-right font-semibold ${
                              variance > 0
                                ? "text-green-700"
                                : variance < 0
                                  ? "text-red-700"
                                  : "text-gray-500"
                            }`}
                          >
                            {variance > 0 ? "+" : ""}
                            {quantity(item.variance)}
                          </td>
                          <td>{reasonLabel(item.reason) || "—"}</td>
                          <td>{item.countedByName}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                <span>
                  Showing{" "}
                  {(history.pagination.page - 1) * history.pagination.limit + 1}
                  –
                  {Math.min(
                    history.pagination.page * history.pagination.limit,
                    history.pagination.total,
                  )}{" "}
                  of {history.pagination.total} posted counts
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      void loadHistory(
                        Math.max(1, history.pagination.page - 1),
                        historySearch,
                      )
                    }
                    disabled={historyLoading || history.pagination.page === 1}
                    className="rounded border border-gray-300 px-3 py-1.5 font-medium disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span>
                    Page {history.pagination.page} of{" "}
                    {history.pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      void loadHistory(
                        Math.min(
                          history.pagination.totalPages,
                          history.pagination.page + 1,
                        ),
                        historySearch,
                      )
                    }
                    disabled={
                      historyLoading ||
                      history.pagination.page === history.pagination.totalPages
                    }
                    className="rounded border border-gray-300 px-3 py-1.5 font-medium disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p className="mt-5 rounded-lg border border-dashed py-10 text-center text-sm text-gray-500">
              {historySearch
                ? `No posted counts match “${historySearch}”.`
                : "No stock counts have been posted for this store yet."}
            </p>
          )}
        </section>
      ) : !review ? (
        <section className="rounded-xl bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">
            Select an area
          </h2>
          {loading ? (
            <p className="mt-4 text-sm text-gray-500">Loading count areas…</p>
          ) : areas.length === 0 ? (
            <p className="mt-4 rounded-lg border border-dashed py-10 text-center text-sm text-gray-500">
              No inventory lots are available to count.
            </p>
          ) : (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {areas.map((area) => (
                <button
                  key={area.area}
                  type="button"
                  onClick={() => void openArea(area.area)}
                  className="rounded-lg border border-gray-200 p-4 text-left hover:border-blue-400 hover:bg-blue-50"
                >
                  <div className="font-semibold text-gray-900">{area.area}</div>
                  <div className="mt-1 text-sm text-gray-600">
                    {area.lotCount} lots · {area.productCount} products
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Expected {quantity(area.expectedQuantity)} base units
                  </div>
                  {area.area.trim().toLowerCase() === "unassigned" && (
                    <div className="mt-3 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                      Use search and pages to count selected lots. Assign rack
                      locations during receiving to split this into smaller
                      areas.
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      ) : (
        <>
          <section className="rounded-xl bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Counting area
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {review.area}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setReview(null);
                  setReviewing(false);
                  setError(null);
                  setDraftStatus(null);
                }}
                className="text-sm font-medium text-blue-700"
              >
                Change area
              </button>
            </div>
            {!reviewing && (
              <div className="mt-5 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <label className="min-w-[240px] flex-1 text-xs font-medium uppercase tracking-wide text-gray-600">
                    Find a lot
                    <input
                      aria-label="Search stock count lots"
                      type="search"
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder="Search product, SKU or batch"
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal"
                    />
                  </label>
                  <label className="text-xs font-medium uppercase tracking-wide text-gray-600">
                    Rows per page
                    <select
                      aria-label="Rows per page"
                      value={pageSize}
                      onChange={(event) => {
                        setPageSize(Number(event.target.value));
                        setPage(1);
                      }}
                      className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const next = { ...counts };
                      for (const line of visibleLines) {
                        next[line.lot.lotId] = line.lot.expectedQuantity;
                      }
                      setCounts(next);
                      saveDraft(review.area, next, false);
                      setDraftStatus(
                        `Copied expected quantity to ${visibleLines.length} visible lot${visibleLines.length === 1 ? "" : "s"}`,
                      );
                    }}
                    disabled={visibleLines.length === 0}
                    className="rounded-lg border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    Count visible as expected
                  </button>
                  <button
                    type="button"
                    onClick={() => saveDraft(review.area, counts)}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Save draft
                  </button>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
                  <span
                    aria-label={`${countedLines.length} counted of ${lines.length} total, ${filteredLines.length} matching`}
                  >
                    <strong className="text-gray-900">
                      {countedLines.length}
                    </strong>{" "}
                    counted of {lines.length} total · {filteredLines.length}{" "}
                    matching
                  </span>
                  {draftStatus && (
                    <span role="status" className="font-medium text-green-700">
                      {draftStatus}
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-3">Product</th>
                    <th>Batch / expiry</th>
                    <th>Expected</th>
                    <th>Physical</th>
                    {reviewing && <th>Variance</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {visibleLines.map((line) => (
                    <tr key={line.lot.lotId}>
                      <td className="px-3 py-3">
                        <strong>{line.lot.productName}</strong>
                        <div className="text-xs text-gray-500">
                          {line.lot.sku}
                        </div>
                      </td>
                      <td>
                        {line.lot.batchNumber}
                        <div className="text-xs text-gray-500">
                          {line.lot.expiryDate
                            ? new Date(line.lot.expiryDate).toLocaleDateString(
                                "en-IN",
                              )
                            : "No expiry"}
                        </div>
                      </td>
                      <td>
                        {quantity(line.lot.expectedQuantity)} {line.lot.unit}
                      </td>
                      <td>
                        <input
                          aria-label={`${line.lot.productName} physical quantity`}
                          type="number"
                          min="0"
                          step="0.001"
                          disabled={reviewing}
                          value={line.raw ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            const next = { ...counts };
                            if (value === "") delete next[line.lot.lotId];
                            else next[line.lot.lotId] = value;
                            setCounts(next);
                            saveDraft(review.area, next, false);
                            setDraftStatus(null);
                          }}
                          className="w-32 rounded border border-gray-300 px-2 py-1.5 text-right"
                        />
                      </td>
                      {reviewing && (
                        <td
                          className={
                            line.variance && line.variance !== 0
                              ? "font-semibold text-orange-700"
                              : "text-gray-600"
                          }
                        >
                          {line.variance === null
                            ? "—"
                            : `${line.variance > 0 ? "+" : ""}${line.variance.toFixed(3)}`}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!reviewing && filteredLines.length === 0 && (
              <p className="py-8 text-center text-sm text-gray-500">
                No lots match “{search}”.
              </p>
            )}
            {!reviewing && filteredLines.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                <span>
                  Showing {(safePage - 1) * pageSize + 1}–
                  {Math.min(safePage * pageSize, filteredLines.length)} of{" "}
                  {filteredLines.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setPage((current) => Math.max(1, current - 1))
                    }
                    disabled={safePage === 1}
                    className="rounded border border-gray-300 px-3 py-1.5 font-medium disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span>
                    Page {safePage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setPage((current) => Math.min(totalPages, current + 1))
                    }
                    disabled={safePage === totalPages}
                    className="rounded border border-gray-300 px-3 py-1.5 font-medium disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </section>
          {!reviewing ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-gray-600">
                Blank lots are skipped. You can post a partial count and return
                later.
              </span>
              <button
                type="button"
                onClick={reviewVariances}
                className="rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700"
              >
                Review {countedLines.length} counted lot
                {countedLines.length === 1 ? "" : "s"}
              </button>
            </div>
          ) : (
            <section className="rounded-xl bg-white p-5 shadow-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-medium text-gray-700">
                  Count reason
                  <select
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
                  >
                    <option value="ROUTINE_AREA_COUNT">
                      Routine area count
                    </option>
                    <option value="CYCLE_COUNT">Cycle count</option>
                    <option value="DISCREPANCY_RECOUNT">
                      Discrepancy recount
                    </option>
                    <option value="DAMAGE_REVIEW">Damage review</option>
                  </select>
                </label>
                <div className="rounded-lg bg-gray-50 p-4">
                  <div className="text-xs uppercase text-gray-500">
                    Review summary
                  </div>
                  <div className="mt-1 font-semibold">
                    {varianceLines.length}{" "}
                    {varianceLines.length === 1 ? "variance" : "variances"}{" "}
                    across {countedLines.length} counted{" "}
                    {countedLines.length === 1 ? "lot" : "lots"}
                  </div>
                </div>
              </div>
              {varianceLines.length > 0 && (
                <p className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
                  Posting will write immutable count movements against the
                  reviewed balance versions.
                </p>
              )}
              <div className="mt-5 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setReviewing(false);
                  }}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 font-medium text-gray-700"
                >
                  Back to counts
                </button>
                <button
                  type="button"
                  onClick={() => void postArea()}
                  disabled={posting}
                  className="rounded-lg bg-green-600 px-6 py-2.5 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {posting
                    ? `Posting ${Object.keys(posted).length}/${countedLines.length}…`
                    : `Post ${countedLines.length} counted lot${countedLines.length === 1 ? "" : "s"}`}
                </button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
